import Icon1 from "@assets/icons/Icon";
import { CopiableInfosSection } from "./CopiableInfosSection";
import { MemberAssignmentBlock } from "@components/ui/blocks/MemberAssignmentBlock";
import { getLocalFromFlutter } from "@services/timeHelpers";
import { Project } from "types";
import { useState, useEffect } from "react";
import { showNotification } from "context/notification.service";
import { colors } from "sexy";
import { usersApi } from "@services/api/users";

function getRelativeTime(value: any): string {
  if (!value) return "";
  let date: Date;
  if (value?.seconds != null) {
    date = new Date(value.seconds * 1000);
  } else if (typeof value === "string") {
    date = new Date(value);
  } else {
    return "";
  }
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 2) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  if (hours < 24) return `il y a ${hours}h`;
  if (days === 1) return "hier";
  if (days < 30) return `il y a ${days} j`;
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const CardHeader = ({ icon, title }: { icon: string; title: string }) => (
  <div className="flex flex-row items-center justify-between mb-2">
    <div className="flex flex-row items-center gap-2">
      <Icon1 name={icon} size={22} />
      <div className="text-h2  text-black">{title}</div>
    </div>
    <div className="cursor-pointer p-1 rounded-md hover:bg-neutral-100 transition-colors">
      <Icon1 name="menu" size={16} />
    </div>
  </div>
);

const AddressRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-row items-center text-h2 text-neutral-400">
    <div className="w-100">{label}</div>
    <div className="flex-1 ">{value}</div>
  </div>
);

export const InfosSection = ({
  project,
  setProject,
}: {
  project: Project;
  setProject?: (project: Project) => void;
}) => {
  const [addressCopied, setAddressCopied] = useState(false);
  const [contactLastActive, setContactLastActive] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const contactsWithUser = project.infos.client.contacts.filter(
      (c) => c.user_id,
    );
    if (contactsWithUser.length === 0) return;
    contactsWithUser.forEach(async (contact) => {
      try {
        const user = await usersApi.get(contact.user_id!);
        const raw = user.activity?.last_active_time;
        const relative = getRelativeTime(raw);
        if (relative) {
          setContactLastActive((prev) => ({
            ...prev,
            [contact.user_id!]: relative,
          }));
        }
      } catch {
        // ignore
      }
    });
  }, [project.infos.client.contacts]);

  // Card 1: Informations dossier
  const generalFields = [
    { label: "Adresse", value: project.infos.general.address || "" },
    { label: "Code Postal", value: project.infos.general.postal || "" },
    { label: "Ville", value: project.infos.general.city || "" },
  ].filter((o) => o.value);

  const fullAddress = generalFields.map((f) => f.value).join(", ");

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(fullAddress);
    showNotification({
      type: "success",
      message: "Adresse copiée dans le presse-papier !",
    });
    setAddressCopied(true);
    setTimeout(() => setAddressCopied(false), 300);
  };

  // Card 2: Informations client
  const dateOptions = [
    {
      label: "Création",
      value: project.infos.general.creation_date
        ? getLocalFromFlutter(project.infos.general.creation_date)
        : "",
    },
    {
      label: "Activité",
      value: project.infos.general.last_updated
        ? getLocalFromFlutter(project.infos.general.last_updated)
        : "",
    },
  ];

  return (
    <div className="flex flex-col w-400 gap-2.5 flex-shrink-0">
      {/* Card 1 - Informations dossier */}
      <div className="flex flex-col bg-white rounded-xl p-4 shadow-lg border">
        <CardHeader icon="folder" title="Informations dossier" />
        <div className="flex flex-row items-center">
          <div className="flex flex-col flex-1">
            {generalFields.map((field, index) => (
              <AddressRow key={index} label={field.label} value={field.value} />
            ))}
          </div>
          <div
            onClick={handleCopyAddress}
            className="flex items-center justify-center w-8 cursor-pointer self-center"
          >
            <Icon1
              animated={true}
              name="copy"
              size={addressCopied ? 24 : 20}
              color={addressCopied ? colors.green_400 : colors.neutral_300}
            />
          </div>
        </div>
        {project.infos.general.notes && (
          <>
            <div className="border-t border-neutral-200 my-3 " />
            <div className="text-h3 text-neutral-400">
              {project.infos.general.notes}
            </div>
          </>
        )}
      </div>

      {/* Card 2 - Informations client */}
      <div className="flex flex-col bg-white rounded-xl p-4 shadow-lg border">
        <CardHeader icon="client" title="Informations client" />
        <CopiableInfosSection options={dateOptions} width="w-70" />
        {project.infos.client.contacts.map((contact, index) => {
          const contactOptions: { label: string; value: string }[] = [];
          if (contact.email) {
            contactOptions.push({ label: "Email", value: contact.email });
          }
          if (contact.phone_number) {
            contactOptions.push({ label: "Tel", value: contact.phone_number });
          }
          return (
            <div
              key={index}
              className="flex flex-col gap-1 mt-2 p-3 border border-neutral-200 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div className="text-h3 font-medium text-neutral-400">
                  {contact.prefix} {contact.first_name} {contact.last_name}
                </div>
                {contact.user_id && contactLastActive[contact.user_id] && (
                  <div className="text-h5 text-neutral-400 flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    {contactLastActive[contact.user_id]}
                  </div>
                )}
              </div>
              <CopiableInfosSection options={contactOptions} width="w-70" />
            </div>
          );
        })}
      </div>

      {/* Card 3 - Membres de l'équipe */}
      <div className="flex flex-col bg-white rounded-xl p-4 shadow-lg border">
        <CardHeader icon="users" title="Membres de l'équipe" />
        <MemberAssignmentBlock
          team={project.infos.team}
          onTeamChange={(team) => {
            if (setProject) {
              setProject({
                ...project,
                infos: { ...project.infos, team },
              });
            }
          }}
        />
      </div>
    </div>
  );
};
